import type { ResumeRoleBullet } from '@careeropt/schema';
import type { AchievementRecord } from './api';

/**
 * Pure résumé-bullet resolution logic for RoleResumeSection (the per-era
 * "read as résumé" fragment, wiring target #1) — no React, no DOM. Extracted
 * so the served-bullet-wins / verbatim-headline-fallback decision is unit-
 * testable on its own, mirroring corpusGrouping.ts's own extraction rationale
 * (Corpus.tsx's card-canvas grouping logic pulled out for the same reason).
 *
 * Mirrors apps/web's RoleResumeSection.tsx inline bullet-building logic
 * exactly in RULE:
 *   - only ACTIVE cards (status !== 'merged') become a resolved bullet row —
 *     a soft-merged (processed/superseded) card is provenance-kept but never
 *     shown as a resume line, same as the composer treats it.
 *   - a card with a served (ghostwritten) bullet whose `text` is non-blank
 *     after trimming uses that served text + the served `kpi` (which may
 *     itself be null — no fallback to the card's own kpiValue in that branch,
 *     since the served bullet is the authoritative gated synthesis).
 *   - a card with NO served bullet (document not loaded, no matching role, or
 *     the model's ghostwrite for this card was empty/ungrounded) falls back
 *     to the card's own verbatim `headline` + its own `kpiValue`.
 * NO INVENTION: both branches only ever surface real served values — nothing
 * here composes, orders-by-model, or fabricates text.
 */
export interface ResolvedResumeBullet {
  achievementId: string;
  text: string;
  kpi: string | null;
}

/** Only active cards belong in a résumé read (mirrors the composer + apps/
 *  web's identical filter). */
export function activeResumeCards(items: AchievementRecord[]): AchievementRecord[] {
  return items.filter((a) => a.status !== 'merged');
}

/** Index served bullets by achievementId for O(1) per-card lookup. */
export function indexServedBullets(
  servedBullets: ResumeRoleBullet[] | undefined,
): Map<string, ResumeRoleBullet> {
  const m = new Map<string, ResumeRoleBullet>();
  for (const b of servedBullets ?? []) m.set(b.achievementId, b);
  return m;
}

/** Resolve one card's display row: served ghostwritten text (+ its own kpi)
 *  when present and non-blank, else the card's verbatim headline (+ its own
 *  kpiValue). */
export function resolveResumeBullet(
  achievement: AchievementRecord,
  served: ResumeRoleBullet | undefined,
): ResolvedResumeBullet {
  const hasServedText = Boolean(served?.text?.trim());
  return {
    achievementId: achievement.id,
    text: hasServedText ? (served as ResumeRoleBullet).text : achievement.headline,
    kpi: hasServedText ? (served?.kpi ?? null) : achievement.kpiValue,
  };
}

/** Build the full ordered set of resolved display rows for one era: filter to
 *  active cards, then resolve each against its served bullet (if any). */
export function resolveResumeBullets(
  items: AchievementRecord[],
  servedBullets: ResumeRoleBullet[] | undefined,
): ResolvedResumeBullet[] {
  const servedByAch = indexServedBullets(servedBullets);
  return activeResumeCards(items).map((a) => resolveResumeBullet(a, servedByAch.get(a.id)));
}
